const DASHBOARD_ROLES = new Set([
  'ADMIN',
  'SUPER_ADMIN',
  'CUSTOMER',
  'COLLECTION_EXECUTIVE',
  'FRONT_DESK',
  'SETTLEMENT_MANAGER',
  'FINANCE_MANAGER',
  'DEMO_EXECUTIVE',
]);

const ROLE_LABELS = {
  ADMIN: 'Company Admin',
  SUPER_ADMIN: 'Super Admin',
  CUSTOMER: 'Customer',
  COLLECTION_EXECUTIVE: 'Collection Executive',
  FRONT_DESK: 'Front Desk',
  SETTLEMENT_MANAGER: 'Settlement Manager',
  FINANCE_MANAGER: 'Finance Manager',
  DEMO_EXECUTIVE: 'Demo Executive',
  VISITOR: 'Visitor',
  GUEST: 'Guest',
};

export function getRedirectBase() {
  return process.env.REACT_APP_REDIRECT_URL || 'https://testapp.chitbid.com';
}

export function storeAuthSession(result, mobile) {
  if (!result?.tokens) return false;
  localStorage.setItem('access-token', result.tokens.accessToken);
  localStorage.setItem('role', btoa(result.user.role.name));
  localStorage.setItem('refreshToken', result.tokens.refreshToken);
  localStorage.setItem('userId', result.user._id);
  if (mobile) localStorage.setItem('mobile', mobile);
  return true;
}

export function clearLandingSession() {
  localStorage.removeItem('access-token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('role');
  localStorage.removeItem('userId');
  localStorage.removeItem('mobile');
}

export function clearAuthSession() {
  clearLandingSession();
}

export function handoffToApp({ target } = {}) {
  const url = buildReconnectUrl({ target });
  window.open(url, '_blank');
  clearLandingSession();
}

export function openAppUrl(url) {
  window.open(url, '_blank');
}

export function buildReconnectUrl({ target } = {}) {
  const redirectUrl = getRedirectBase();
  const params = new URLSearchParams({
    accessToken: localStorage.getItem('access-token') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    roleName: localStorage.getItem('role') || '',
  });
  if (target) params.set('target', target);
  return `${redirectUrl}/reconnect/?${params.toString()}`;
}

export function buildForceChangePasswordUrl(result) {
  const redirectUrl = getRedirectBase();
  const params = new URLSearchParams({
    email: result.email || '',
    userId: result.userId || '',
  });
  return `${redirectUrl}/force-change-password?${params.toString()}`;
}

export function resolveLandingAction(result) {
  if (result?.landingRedirect) {
    return result.landingRedirect;
  }

  const role = result?.user?.role?.name;
  const status = result?.currentStatus;

  if (role === 'VISITOR') {
    if (status === 'registration_pending') {
      return {
        action: 'show_registration_form',
        role,
        message: 'Complete your company registration.',
      };
    }
    return {
      action: 'continue_onboarding',
      role,
      message: 'Continue your company onboarding.',
    };
  }

  if (DASHBOARD_ROLES.has(role)) {
    const label = ROLE_LABELS[role] || role;
    return {
      action: 'go_to_dashboard',
      role,
      message: `This mobile is linked to your ${label} account. Opening your dashboard.`,
    };
  }

  return {
    action: 'go_to_login',
    role,
    message: `This mobile is linked to a ${ROLE_LABELS[role] || role} account. Company registration is not available here.`,
  };
}

export function getReconnectTarget(action, role) {
  if (action === 'continue_onboarding') return '/visitor/visitorPage';
  if (action === 'go_to_dashboard' || DASHBOARD_ROLES.has(role)) return '/dashboard';
  if (action === 'go_to_login') return '/login';
  return '/visitor/visitorPage';
}

export function showExistingAccountHint(sendResult) {
  const meta = sendResult?.data?.result;
  if (!meta?.existingAccount) return null;

  if (meta.canRegisterCompany) {
    return 'Welcome back. Complete your company registration after OTP verification.';
  }

  const label = ROLE_LABELS[meta.role] || meta.role || 'registered';
  if (meta.role === 'VISITOR') {
    return 'Welcome back. You will continue your company onboarding after OTP verification.';
  }
  if (DASHBOARD_ROLES.has(meta.role)) {
    return `This mobile is linked to a ${label} account. After OTP you will be taken to your dashboard.`;
  }
  return `This mobile is linked to a ${label} account. Company registration is not available on this page.`;
}

export function handleVerifyResponse(updateApi, mobile, callbacks = {}) {
  const { onShowRegistrationForm, onCloseModals } = callbacks;

  if (!updateApi || updateApi.status >= 400) {
    return {
      ok: false,
      message: updateApi?.data?.msg || 'OTP verification failed',
    };
  }

  const result = updateApi.data?.result;

  if (result?.mustChangePassword) {
    openAppUrl(buildForceChangePasswordUrl(result));
    return {
      ok: true,
      action: 'force_change_password',
      message: result.landingRedirect?.message || 'Please set a new password to continue.',
    };
  }

  const landing = resolveLandingAction(result);

  if (landing.action === 'show_registration_form') {
    if (!storeAuthSession(result, mobile)) {
      return { ok: false, message: 'Could not sign you in. Please try again.' };
    }
    onCloseModals?.();
    onShowRegistrationForm?.();
    return { ok: true, action: landing.action, message: landing.message };
  }

  if (landing.action === 'go_to_login') {
    clearLandingSession();
    openAppUrl(`${getRedirectBase()}/login`);
    return { ok: true, action: landing.action, message: landing.message };
  }

  if (!storeAuthSession(result, mobile)) {
    return { ok: false, message: 'Could not sign you in. Please try again.' };
  }

  onCloseModals?.();
  const target = getReconnectTarget(landing.action, landing.role);
  handoffToApp({ target });
  return { ok: true, action: landing.action, message: landing.message };
}
